import axios, { CancelToken } from 'apis';
import { sessionService } from 'redux-react-session';
import { getThunkActionCreator } from 'redux-thunk-routine';
import {
  getTowlotVehiclesRoutine,
  getTowlotVehicleRoutine,
  clearLocalTowlotVehiclesRoutine,
  clearLocalActiveTowlotVehicleRoutine,
  createTowlotVehicleRoutine,
  retryTowlotVehicleRoutine,
  getAuctionTowlotVehiclesRoutine,
  clearLocalAuctionTowlotVehiclesRoutine
} from 'actions';

const auctionTowlotAttributes = [
  'id',
  'towlot_status',
  { vehicle: ['id', 'vin', 'make', 'model', 'year', 'license', { order: ['number'] }] }
];

const attributes = [
  'id', 'towlot_status', 'engine', 'car_title', 'car_type', 'keys_type',
  'transmission', 'stock', 'color', 'description', 'starting_price',
  'reserve_price', 'source', 'youtube_link', 'exterior', 'interior',
  'mechanical', 'options', 'terms_of_sale', 'towlot_vehicle_id',
  'last_synced_at', 'image_summary', 'created_at',
  { vehicle: ['id', 'vin', 'make', 'model', 'year', { order: ['number'] }] }
];

const detailAttributes = [
  ...attributes,
  { images: ['id', 'url', 'filename', 'content_type', 'is_feature', { representations: ['name', 'url'] }] },
  { vehicle: [
    'id', 'vin', 'make', 'model', 'year', 'color', 'drive_type',
    'license', 'license_state', 'odometer', 'status', 'storage_days',
    'stored_at', 'storage_duration', 'suggested_days_for_auction',
    { order: [
      'id', 'number', 'status', 'source', 'created_at',
      'caller_name', 'caller_phone',
      'member_first_name', 'member_last_name', 'member_phone',
      'keys_availability', 'driver_attendance',
      'internal_notes', 'dispatcher_notes',
      'incident_location', 'destination_location',
      { account: ['id', 'name', 'account_type'] },
      { customer: ['id', 'name', 'email', 'phone'] },
      { user: ['id', 'first_name', 'last_name', 'phone'] },
      { dispatcher: ['id', 'first_name', 'last_name'] },
      { truck: ['id', 'make', 'model', 'year'] },
      { services: ['id', 'name'] },
      { company_location: ['id', 'name'] },
      { invoice: ['id', 'status', 'total_amount', 'paid_amount', { payments: ['id', 'payment_type', 'amount'] }] },
      { incident_location: ['longitude', 'latitude', 'address'] },
      { destination_location: ['longitude', 'latitude', 'address'] }
    ] }
  ] }
];

export const getAuctionTowlotVehicles = getThunkActionCreator(
  getAuctionTowlotVehiclesRoutine,
  async ({ offset = 0, limit = 25, query = null } = {}) => {
    getAuctionTowlotVehicles.cancel && getAuctionTowlotVehicles.cancel();
    const { token: access_token } = await sessionService.loadSession();

    return await axios.get('/user/towlot_vehicles', {
      params: {
        access_token,
        offset,
        limit,
        attributes: auctionTowlotAttributes,
        ...(!!query && { query })
      },
      cancelToken: new CancelToken(function executor(cancel) { getAuctionTowlotVehicles.cancel = cancel })
    });
  }
);

export const clearLocalAuctionTowlotVehicles = getThunkActionCreator(
  clearLocalAuctionTowlotVehiclesRoutine,
  async () => true
);

export const getTowlotVehicles = getThunkActionCreator(
  getTowlotVehiclesRoutine,
  async ({ offset = 0, limit = 25, query = null, statuses = [], created_at_gt = null, created_at_lt = null } = {}) => {
    getTowlotVehicles.cancel && getTowlotVehicles.cancel();
    const { token: access_token } = await sessionService.loadSession();

    return await axios.get('/user/towlot_vehicles', {
      params: {
        access_token,
        offset,
        limit,
        attributes,
        ...(!!query && { query }),
        ...(!!!query && !!statuses.length && { statuses }),
        ...(!!created_at_gt && { created_at_gt }),
        ...(!!created_at_lt && { created_at_lt }),
      },
      cancelToken: new CancelToken(function executor(cancel) { getTowlotVehicles.cancel = cancel })
    });
  }
);

export const getTowlotVehicle = getThunkActionCreator(
  getTowlotVehicleRoutine,
  async (id) => {
    const { token: access_token } = await sessionService.loadSession();

    return await axios.get(`/user/towlot_vehicles/${id}`, {
      params: { access_token, attributes: detailAttributes }
    });
  }
);

export const clearLocalTowlotVehicles = getThunkActionCreator(
  clearLocalTowlotVehiclesRoutine,
  async () => true
);

export const clearLocalActiveTowlotVehicle = getThunkActionCreator(
  clearLocalActiveTowlotVehicleRoutine,
  async () => true
);

export const createTowlotVehicle = getThunkActionCreator(
  createTowlotVehicleRoutine,
  async (payload) => {
    const { token: access_token } = await sessionService.loadSession();

    return await axios.post('/user/towlot_vehicles', { ...payload, access_token, attributes });
  }
);

export const retryTowlotVehicle = getThunkActionCreator(
  retryTowlotVehicleRoutine,
  async ({ id, ...payload }) => {
    const { token: access_token } = await sessionService.loadSession();

    return await axios.post(`/user/towlot_vehicles/${id}/retry`, { ...payload, access_token, attributes: detailAttributes });
  }
);

